// components/ThreadShareDialog.js
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { getCleanDisplayName } from './activityHelpers';

export default function ThreadShareDialog({ open, onClose, activity }) {
  const [copied, setCopied] = useState(false);
  
  if (!activity) return null;
  
  const shareUrl = `https://www.useblitz.co/thread/${activity.activity_id}`;
  const shareTitle = `Thread by ${getCleanDisplayName(activity)}`;
  const shareDescription = activity.activity_name?.substring(0, 200) + 
    (activity.activity_name?.length > 200 ? "..." : "");

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareDescription,
          url: shareUrl,
        });
        onClose();
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    }
  };

  const socialPlatforms = [
    {
      name: 'Twitter',
      icon: <TwitterIcon />,
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'Facebook',
      icon: <FacebookIcon />,
      color: '#4267B2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInIcon />,
      color: '#0077B5',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'WhatsApp',
      icon: <WhatsAppIcon />,
      color: '#25D366',
      url: `https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`
    }
  ];

  const handleSocialShare = (url) => {
    window.open(url, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share Thread</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
              {shareTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {shareDescription}
            </Typography>
          </Box>

          <Box display="flex" gap={1} mb={3} sx={{ 
            p: 2, 
            border: 1, 
            borderColor: 'divider', 
            borderRadius: 1,
            bgcolor: 'background.paper'
          }}>
            <Typography 
              variant="body2" 
              sx={{ flex: 1, wordBreak: 'break-all', alignSelf: 'center' }}
            >
              {shareUrl}
            </Typography>
            <Button
              size="small"
              startIcon={<CopyIcon />}
              onClick={handleCopyLink}
              variant={copied ? "contained" : "outlined"}
              color={copied ? "success" : "primary"}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </Box>

          {navigator.share && (
            <Box sx={{ mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleNativeShare}
              >
                Share via device
              </Button>
            </Box>
          )}

          <Typography variant="subtitle2" gutterBottom>Share on social media</Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {socialPlatforms.map((platform) => (
              <Button
                key={platform.name}
                variant="outlined"
                startIcon={platform.icon}
                onClick={() => handleSocialShare(platform.url)}
                sx={{ 
                  borderColor: platform.color,
                  color: platform.color,
                  '&:hover': {
                    borderColor: platform.color,
                    bgcolor: alpha(platform.color, 0.1)
                  }
                }}
              >
                {platform.name}
              </Button>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}